import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { getDebugUi, subscribeDebugUi } from '../utils/debugLog';
import { readPower, loadSamples, clearSamples, type PowerView } from '../utils/powerStats';
import { Z_INDEX } from '../constants/zIndex';

const POLL_MS = 15_000;

/**
 * Batterijmeter voor een testtoestel. Zichtbaar zolang de diagnostiek op dit toestel aan
 * staat (dezelfde schakelaar als de DebugOverlay), dus nooit bij een bewoner zonder dat
 * iemand het bewust aanzet. Blijft boven de slaapstand staan: daar wordt 's nachts gemeten.
 *
 * Tik = details (APK-versie, helderheid, schermvlaggen, uurverloop).
 */
export function PowerMeter() {
  const ui = useSyncExternalStore(subscribeDebugUi, getDebugUi);
  const [view, setView] = useState<PowerView | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!ui.enabled) return;
    let alive = true;
    const tick = () => {
      void readPower().then((v) => {
        if (alive) setView(v);
      });
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [ui.enabled]);

  if (!ui.enabled || ui.open) return null;

  const v = view;
  const arrow = v?.charging ? '⚡' : '↓';
  const summary = v
    ? [
        v.levelPct != null ? `${v.levelPct}%` : '?%',
        v.currentMa != null ? `${arrow} ${Math.round(v.currentMa)} mA` : v.charging ? '⚡' : null,
        v.watts != null ? `${v.watts.toFixed(1)} W` : null,
        v.drainPctPerHour != null ? `${v.drainPctPerHour > 0 ? '−' : '+'}${Math.abs(v.drainPctPerHour).toFixed(1)} %/u` : null,
        v.hoursLeft != null ? `~${v.hoursLeft.toFixed(1)} u` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '…';

  return (
    <div
      onClick={() => setExpanded((e) => !e)}
      role="button"
      aria-label="Batterijmeter"
      style={{
        position: 'fixed',
        top: 6,
        right: 6,
        zIndex: Z_INDEX.POWER_METER,
        background: 'rgba(0,0,0,0.72)',
        color: '#e6edf3',
        borderRadius: 10,
        padding: expanded ? '10px 12px' : '4px 10px',
        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
        fontSize: 13,
        lineHeight: 1.45,
        maxWidth: expanded ? 420 : undefined,
        userSelect: 'none',
        cursor: 'pointer',
      }}
    >
      <div style={{ whiteSpace: 'nowrap' }}>🔋 {summary}</div>
      {expanded && v && <Details v={v} />}
    </div>
  );
}

function Details({ v }: { v: PowerView }) {
  const r = v.raw ?? {};
  const hourly = hourlyLevels();
  return (
    <div style={{ marginTop: 6, fontSize: 12 }}>
      <Row k="bron" v={v.source === 'native' ? 'APK' : v.source === 'browser' ? 'browser (beperkt)' : 'geen'} />
      <Row k="lader" v={`${v.plugged} · ${r.status ?? '?'}`} />
      <Row k="stroom" v={v.currentMa != null ? `${Math.round(v.currentMa)} mA (ruw ${r.currentNowRaw}, eenheid ${v.currentUnitGuess})` : '—'} />
      <Row k="spanning" v={r.voltageMv != null && r.voltageMv > 0 ? `${(r.voltageMv / 1000).toFixed(2)} V` : '—'} />
      <Row k="temp" v={v.temperatureC != null ? `${v.temperatureC.toFixed(1)} °C` : '—'} />
      <Row
        k="daling"
        v={v.drainPctPerHour != null ? `${v.drainPctPerHour.toFixed(2)} %/u over ${v.drainWindowMin} min` : 'nog te weinig metingen (≥10 min zonder lader)'}
      />
      <Row k="helderheid" v={r.systemBrightness != null ? `${r.systemBrightness}/255${r.autoBrightness ? ' (auto)' : ''} · venster ${r.windowBrightness ?? '?'}` : '—'} />
      <Row k="scherm" v={`aan=${fmtBool(r.interactive)} · keepOn=${fmtBool(r.keepScreenOn)}`} />
      <Row k="energie" v={`besparing=${fmtBool(r.powerSaveMode)} · doze-vrij=${fmtBool(r.ignoringBatteryOptimizations)}`} />
      <Row k="APK" v={r.versionName ? `${r.versionName} (${r.versionCode})` : 'onbekend (APK < 1.0.4 of browser)'} />
      {r.lastUpdateTime ? <Row k="APK bijgewerkt" v={new Date(r.lastUpdateTime).toLocaleString('nl-BE')} /> : null}
      {r.installer !== undefined ? <Row k="installer" v={r.installer || '(adb/onbekend)'} /> : null}
      {r.model ? <Row k="toestel" v={`${r.model} · Android ${r.androidRelease}`} /> : null}
      {hourly.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: '#8aa0b4' }}>niveau per uur</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{hourly.join('  ')}</div>
        </div>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          clearSamples();
        }}
        style={{ marginTop: 8, background: '#1e2a36', color: '#d6dee6', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}
      >
        Metingen wissen
      </button>
    </div>
  );
}

/** "22u 81%" per uur uit de opgeslagen metingen; ⚡ als er in dat uur geladen werd. */
function hourlyLevels(): string[] {
  const samples = loadSamples();
  const out: string[] = [];
  let lastHourKey = '';
  for (const s of samples) {
    const d = new Date(s.t);
    const key = `${d.getDate()}-${d.getHours()}`;
    if (key === lastHourKey) continue;
    lastHourKey = key;
    out.push(`${String(d.getHours()).padStart(2, '0')}u ${s.level}%${s.plugged ? '⚡' : ''}`);
  }
  return out.slice(-24);
}

function fmtBool(b: boolean | undefined): string {
  return b === undefined ? '?' : b ? 'ja' : 'nee';
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ color: '#8aa0b4', minWidth: 96 }}>{k}</span>
      <span style={{ wordBreak: 'break-word' }}>{v}</span>
    </div>
  );
}
